<template>
  <v-row>
    <v-col>
      <v-card class="mb-3">
        <v-card-text class="d-flex flex-wrap justify-space-between align-center">
          <v-btn size="small" variant="text" class="mr-2" @click="router.push('/miqaat-management')">Back to list</v-btn>
          <div class="d-flex flex-wrap align-center">
            <v-btn size="small" variant="outlined" class="mr-2" prepend-icon="mdi-eye" @click="previewRegistration">Preview</v-btn>
            <v-btn size="small" variant="outlined" class="mr-2" @click="openFilters">Filters ({{ activeFilterCount(filters) }})</v-btn>
            <v-btn size="small" variant="outlined" class="mr-2" @click="openExclusions">Exclusions{{ hasExclusions(exclusions) ? ' (set)' : '' }}</v-btn>
            <v-btn size="small" color="primary" @click="saveMiqaat">Save</v-btn>
          </div>
        </v-card-text>
      </v-card>

      <v-card>
        <v-card-text>
          <v-text-field label="Miqaat Name" v-model="form.miqaatName" />
          <RichTextEditor label="Description" v-model="form.description" class="mb-4" />
          <v-row>
            <v-col cols="12" md="3">
              <VueDatePicker v-model="form.startDate" :enable-time-picker="false" auto-apply format="dd/MMM/yyyy" placeholder="Event start" :teleport="true" />
            </v-col>
            <v-col cols="12" md="3">
              <VueDatePicker v-model="form.endDate" :enable-time-picker="false" auto-apply format="dd/MMM/yyyy" placeholder="Event end" :teleport="true" />
            </v-col>
            <v-col cols="12" md="3">
              <VueDatePicker v-model="form.registrationStartDate" :enable-time-picker="false" auto-apply format="dd/MMM/yyyy" placeholder="Reg. start" :teleport="true" />
            </v-col>
            <v-col cols="12" md="3">
              <VueDatePicker v-model="form.registrationEndDate" :enable-time-picker="false" auto-apply format="dd/MMM/yyyy" placeholder="Reg. end" :teleport="true" />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="4">
              <v-text-field label="Registration Commitment Amount" v-model.number="form.registrationCommitmentAmount" type="number" />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field label="Accommodation Amount" v-model.number="form.accommodationAmount" type="number" />
            </v-col>
            <v-col cols="12" md="4" class="d-flex align-center">
              <v-checkbox v-model="form.isActive" label="Active" density="compact" hide-details />
            </v-col>
          </v-row>
          <v-checkbox
            v-model="form.allowMemberSelfRegistration"
            label="Allow members to self-register"
            density="compact"
            hide-details
          />
          <v-checkbox
            :model-value="form.niazCommitmentRequired"
            label="Niaz commitment required"
            density="compact"
            hide-details
            @update:model-value="onRequiredChanged"
          />
          <v-checkbox
            :model-value="form.niazCommitmentCompulsory"
            label="Niaz commitment compulsory"
            density="compact"
            hide-details
            @update:model-value="onCompulsoryChanged"
          />
        </v-card-text>
      </v-card>

      <v-card class="mt-3">
        <v-card-text>
          <div class="d-flex flex-wrap justify-space-between align-center mb-3">
            <div class="text-h6">Survey forms</div>
            <v-btn size="small" variant="outlined" @click="addSurveyLink">Add survey</v-btn>
          </div>

          <v-alert v-if="!surveyLinks.length" type="info" variant="tonal" density="compact" class="mb-3">
            No survey forms attached. Attach existing surveys from Survey Forms.
          </v-alert>

          <div
            v-for="(link, index) in surveyLinks"
            :key="link.miqaatSurveyId || `new-${index}`"
            class="mb-4 pa-3"
            style="border: 1px solid rgba(0,0,0,0.12); border-radius: 4px;"
          >
            <v-row dense>
              <v-col cols="12" md="5">
                <v-autocomplete
                  label="Survey"
                  v-model="link.surveyId"
                  :items="surveyOptions"
                  item-title="title"
                  item-value="surveyID"
                  density="compact"
                  hide-details
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  label="Timing"
                  v-model="link.timing"
                  :items="timingOptions"
                  density="compact"
                  hide-details
                  @update:model-value="onTimingChanged(link)"
                />
              </v-col>
              <v-col cols="6" md="2">
                <v-text-field
                  label="Sort"
                  v-model.number="link.sortOrder"
                  type="number"
                  density="compact"
                  hide-details
                />
              </v-col>
              <v-col cols="6" md="2" class="d-flex align-center">
                <v-checkbox
                  v-model="link.isRequired"
                  label="Required"
                  density="compact"
                  hide-details
                  :disabled="link.timing !== 'AtRegistration'"
                />
              </v-col>
            </v-row>
            <div class="mt-2">
              <v-btn size="small" variant="text" color="error" @click="removeSurveyLink(index)">Remove</v-btn>
            </div>
          </div>

          <div class="d-flex justify-end">
            <v-btn size="small" color="primary" @click="saveSurveyLinks">Save survey links</v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>

  <MiqaatFiltersDialog ref="dlgFilters" v-model="filters" :miqaat-id="miqaatId" />
  <MiqaatExclusionsDialog ref="dlgExclusions" v-model="exclusions" :miqaat-id="miqaatId" />
</template>

<script setup>
import { reactive, ref, inject, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  getMiqaat,
  updateMiqaat,
  updateMiqaatSurveys,
} from "@/services/miqaatService";
import { getAll as getAllSurveys } from "@/services/surveyService";
import MiqaatFiltersDialog from "./components/MiqaatFiltersDialog.vue";
import MiqaatExclusionsDialog from "./components/MiqaatExclusionsDialog.vue";
import RichTextEditor from "@/components/RichTextEditor.vue";
import { emptySurveyFilters, filtersToRows, rowsToFilters, activeFilterCount } from "@/views/SurveyForms/utils/surveyFilterUtils";
import { emptySurveyExclusions, exclusionsFromApi, hasExclusions, exclusionsToPayload } from "@/views/SurveyForms/utils/surveyExclusionUtils";

const route = useRoute();
const router = useRouter();
const loader = inject("loader");
const snackbar = inject("snackBar");

const miqaatId = Number(route.params.id);
const dlgFilters = ref(null);
const dlgExclusions = ref(null);

const timingOptions = [
  { title: "At registration", value: "AtRegistration" },
  { title: "After registration", value: "AfterRegistration" },
];

const form = reactive({
  miqaatName: "",
  description: "",
  startDate: null,
  endDate: null,
  registrationStartDate: null,
  registrationEndDate: null,
  registrationCommitmentAmount: 0,
  accommodationAmount: 0,
  isActive: true,
  allowMemberSelfRegistration: true,
  niazCommitmentRequired: true,
  niazCommitmentCompulsory: true,
});

const filters = ref(emptySurveyFilters());
const exclusions = ref(emptySurveyExclusions());
const surveyLinks = ref([]);
const surveyOptions = ref([]);

function onRequiredChanged(value) {
  form.niazCommitmentRequired = value;
  if (!value) form.niazCommitmentCompulsory = false;
}

function onCompulsoryChanged(value) {
  form.niazCommitmentCompulsory = value;
  if (value) form.niazCommitmentRequired = true;
}

function onTimingChanged(link) {
  if (link.timing !== "AtRegistration") link.isRequired = false;
}

function addSurveyLink() {
  surveyLinks.value.push({
    miqaatSurveyId: null,
    surveyId: null,
    timing: "AtRegistration",
    isRequired: false,
    sortOrder: surveyLinks.value.length + 1,
  });
}

function removeSurveyLink(index) {
  surveyLinks.value.splice(index, 1);
}

async function loadData() {
  loader.show();
  try {
    const [data, surveys] = await Promise.all([
      getMiqaat(miqaatId),
      getAllSurveys().catch(() => []),
    ]);
    Object.assign(form, {
      miqaatName: data.miqaatName,
      description: data.description,
      startDate: data.startDate,
      endDate: data.endDate,
      registrationStartDate: data.registrationStartDate,
      registrationEndDate: data.registrationEndDate,
      registrationCommitmentAmount: data.registrationCommitmentAmount,
      accommodationAmount: data.accommodationAmount,
      isActive: data.isActive,
      allowMemberSelfRegistration: data.allowMemberSelfRegistration ?? true,
      niazCommitmentRequired: data.niazCommitmentRequired ?? true,
      niazCommitmentCompulsory: data.niazCommitmentCompulsory ?? true,
    });
    filters.value = rowsToFilters(data.filters);
    exclusions.value = exclusionsFromApi(data);
    surveyOptions.value = surveys || [];
    surveyLinks.value = (data.surveys || []).map((s) => ({
      miqaatSurveyId: s.miqaatSurveyId,
      surveyId: s.surveyId,
      timing: s.timing || "AtRegistration",
      isRequired: !!s.isRequired,
      sortOrder: s.sortOrder ?? 0,
    }));
  } catch (error) {
    snackbar.error(error);
  } finally {
    loader.hide();
  }
}

function openFilters() {
  dlgFilters.value.show();
}

function openExclusions() {
  dlgExclusions.value.show();
}

function previewRegistration() {
  const url = router.resolve({
    path: `/miqaat-registration/${miqaatId}`,
    query: { preview: "1" },
  }).href;
  window.open(url, "_blank");
}

async function saveMiqaat() {
  loader.show();
  try {
    await updateMiqaat(miqaatId, {
      ...form,
      filters: filtersToRows(filters.value),
      ...exclusionsToPayload(exclusions.value),
    });
    snackbar.success("Miqaat saved");
  } catch (error) {
    snackbar.error(error);
  } finally {
    loader.hide();
  }
}

async function saveSurveyLinks() {
  const invalid = surveyLinks.value.some((x) => !x.surveyId);
  if (invalid) {
    snackbar.error("Please select a survey for each row");
    return;
  }

  const ids = surveyLinks.value.map((x) => x.surveyId);
  if (new Set(ids).size !== ids.length) {
    snackbar.error("Each survey can only be linked once");
    return;
  }

  loader.show();
  try {
    const result = await updateMiqaatSurveys(
      miqaatId,
      surveyLinks.value.map((x) => ({
        miqaatSurveyId: x.miqaatSurveyId || undefined,
        surveyId: x.surveyId,
        timing: x.timing,
        isRequired: x.timing === "AtRegistration" ? !!x.isRequired : false,
        sortOrder: x.sortOrder || 0,
      }))
    );
    surveyLinks.value = (result.surveys || []).map((s) => ({
      miqaatSurveyId: s.miqaatSurveyId,
      surveyId: s.surveyId,
      timing: s.timing || "AtRegistration",
      isRequired: !!s.isRequired,
      sortOrder: s.sortOrder ?? 0,
    }));
    snackbar.success("Survey links saved");
  } catch (error) {
    snackbar.error(error);
  } finally {
    loader.hide();
  }
}

onMounted(loadData);
</script>
